import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import Text from "../../../../../components/Text";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_TEXT_FONT_WEIGHT,
  getIdFromObj,
  getTrimmedUuid,
} from "../../../../../utils/utils";
import EraComputedProcedureSubTable, {
  CptDenialField,
} from "./EraComputedProcedureSubTable";
import { PatientProcedureCode } from "../../../../../services";
import {
  ERA_POSTING_STATUS_BADGE,
  EraClaimDisplayStatus,
  EraPostingDisplayStatus,
  normalizeEraPostingStatus,
} from "./constants";

export interface EraClaimRow {
  claimId?: string;
  payersClaimId?: string;
  claimStatus?: string;
  dateOfService?: string;
  posted?: boolean;
  postedAt?: string;
  // ECRH-4700 — backend-derived claim posting status ("Not Paid" /
  // "Partially Paid" / "Paid") reflecting how many CPT lines are posted.
  postingStatus?: string;
  patientName?: string;
  provider?: string;
  paidAs?: string;
  chargeAmount?: number;
  groupCode?: string;
  reasonCode?: string;
  patientProcedureCodes?: PatientProcedureCode[];
  providerName?: string;
  // permissive — backend shape varies
  [k: string]: unknown;
}

const headerSx = {
  bgcolor: "grey.50",
  "& th": {
    fontSize: DEFAULT_FONTSIZE,
    fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
    py: 1.2,
    px: 1,
    borderBottom: "1px solid",
    borderColor: "grey.300",
    whiteSpace: "nowrap",
  },
};

const rowSx = {
  "& td": {
    py: 1,
    px: 1,
    borderBottom: "1px solid",
    borderColor: "grey.200",
  },
};

// ECRH-5170 — key must stay stable when a claim moves from `pendingClaims`
// (carries `claimId`) into `claims` (carries `uuid`/`payersClaimId` but no
// `claimId`) after the first CPT is posted. `payersClaimId` is present in both
// shapes, so prefer it — otherwise the expanded row collapses on post.
export const claimKey = (claim: EraClaimRow): string =>
  claim.payersClaimId ??
  (claim.uuid as string | undefined) ??
  claim.claimId ??
  "";

const getEraClaimDisplayStatus = (
  claim: EraClaimRow
): EraClaimDisplayStatus => {
  if (claim.posted || claim.postedAt) return "POSTED";
  const status = (claim.claimStatus ?? "").toUpperCase();
  if (status === "POSTED") return "POSTED";
  if (status === "DENIED" || status === "REJECTED") return "DENIED";
  return "PAID";
};

const isClaimEligibleForPost = (claim: EraClaimRow): boolean => {
  return !!claim?.posted;
  // const display = getEraClaimDisplayStatus(claim);
  // return display === "PAID" || display === "DENIED";
};

// ECRH-4700 — claim posting status. Prefer the backend `postingStatus`; if it
// is absent, derive from how many CPT lines carry `posted: true`.
const getClaimPostingDisplay = (
  claim: EraClaimRow
): EraPostingDisplayStatus => {
  const fromBackend = normalizeEraPostingStatus(claim.postingStatus);
  if (fromBackend) return fromBackend;

  const pcs = claim.patientProcedureCodes ?? [];
  const postedCount = pcs.filter((pc) => pc?.posted).length;
  if (pcs.length > 0 && postedCount === pcs.length) return "PAID";
  if (postedCount > 0) return "PARTIALLY_PAID";

  const legacy = getEraClaimDisplayStatus(claim);
  if (legacy === "DENIED") return "DENIED";
  if (claim.posted) return "PAID";
  return "NOT_PAID";
};

const PostingStatusBadge = ({
  status,
}: {
  status: EraPostingDisplayStatus;
}) => {
  const cfg = ERA_POSTING_STATUS_BADGE[status];
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        border: "none",
        fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
        height: 20,
        fontSize: "11px",
        px: 0.5,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ECRH-4700 — replaced by PostingStatusBadge (claim posting status). Kept for
// reference in case the legacy claim-status badge is needed again.
// const StatusBadge = ({ status }: { status: EraClaimDisplayStatus }) => {
//   const cfg = ERA_CLAIM_STATUS_BADGE[status];
//   return (
//     <Chip
//       size="small"
//       label={cfg.label}
//       sx={{
//         bgcolor: cfg.bg,
//         color: cfg.color,
//         border: "none",
//         fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
//         height: 20,
//         fontSize: "11px",
//         px: 0.5,
//         "& .MuiChip-label": { px: 1 },
//       }}
//     />
//   );
// };

interface EraClaimsRowProps {
  claims: EraClaimRow[];
  selectedClaimIds: Set<string>;
  onToggleSelect: (claimKey: string) => void;
  onToggleSelectAll: (selectAll: boolean) => void;
  // ECRH-4700 — posting is now per CPT line; pcIndex identifies the line.
  onPostCpt: (claim: EraClaimRow, pcIndex: number) => void;
  // ECRH-5475 — edit a CPT line's Denied / Denial Code / Comments fields.
  onEditCpt: (
    claim: EraClaimRow,
    pcIndex: number,
    field: CptDenialField,
    value: boolean | string
  ) => void;
  // onClaimAction: (action: string, claim: EraClaimRow) => void;
  hasPostPermission: boolean;
}

const EraClaimsRow = ({
  claims,
  selectedClaimIds,
  onToggleSelect,
  // onToggleSelectAll,
  onPostCpt,
  onEditCpt,
  // onClaimAction,
  hasPostPermission,
}: EraClaimsRowProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // const eligibleClaims = claims.filter(isClaimEligibleForPost);
  // const eligibleCount = eligibleClaims.length;
  // const selectedEligibleCount = eligibleClaims.filter((c) =>
  //   selectedClaimIds.has(claimKey(c))
  // ).length;

  // const allSelected =
  //   eligibleCount > 0 && selectedEligibleCount === eligibleCount;
  // const someSelected =
  //   selectedEligibleCount > 0 && selectedEligibleCount < eligibleCount;

  if (!claims.length) {
    return (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: "1px dashed",
          borderColor: "grey.300",
          borderRadius: 1,
        }}
      >
        <Text variant="body2" color="text.secondary">
          No claims found for this ERA.
        </Text>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={headerSx}>
            <TableCell width={40} />
            {/* <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                disabled={eligibleCount === 0}
                onChange={(e) => onToggleSelectAll(e.target.checked)}
              />
            </TableCell> */}
            <TableCell>Claim ID</TableCell>
            <TableCell>Payer ICN</TableCell>
            <TableCell>Patient Name</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Paid As</TableCell>
            <TableCell>Total Bill</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {claims.map((claim) => {
            const key = claimKey(claim);
            const status = getClaimPostingDisplay(claim);
            const eligible = isClaimEligibleForPost(claim);
            const isExpanded = !!expanded[key];
            const isSelected = selectedClaimIds.has(key);

            return (
              <ClaimRow
                key={key}
                claim={claim}
                // claimKeyValue={key}
                status={status}
                eligible={eligible}
                isExpanded={isExpanded}
                isSelected={isSelected}
                hasPostPermission={hasPostPermission}
                onToggleExpand={() =>
                  setExpanded((s) => ({ ...s, [key]: !s[key] }))
                }
                onToggleSelect={() => onToggleSelect(key)}
                onPostCpt={(pcIndex) => onPostCpt(claim, pcIndex)}
                onEditCpt={(pcIndex, field, value) =>
                  onEditCpt(claim, pcIndex, field, value)
                }
                // onClaimAction={onClaimAction}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface ClaimRowProps {
  claim: EraClaimRow;
  // claimKeyValue: string;
  status: EraPostingDisplayStatus;
  eligible: boolean;
  isExpanded: boolean;
  isSelected?: boolean;
  hasPostPermission: boolean;
  onToggleExpand?: () => void;
  onToggleSelect: () => void;
  onPostCpt: (pcIndex: number) => void;
  onEditCpt: (
    pcIndex: number,
    field: CptDenialField,
    value: boolean | string
  ) => void;
  // onClaimAction: (action: string, claim: EraClaimRow) => void;
}

const ClaimRow = ({
  claim,
  // claimKeyValue,
  status,
  // eligible,
  isExpanded,
  // isSelected,
  hasPostPermission,
  onToggleExpand,
  // onToggleSelect,
  onPostCpt,
  onEditCpt,
  // onClaimAction,
}: ClaimRowProps) => {
  const totalBill = Number(claim.chargeAmount ?? 0);

  return (
    <>
      <TableRow sx={rowSx}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={onToggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {/* <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            disabled={!eligible}
            onChange={onToggleSelect}
          />
        </TableCell> */}
        <TableCell>
          <Text isTabletext>
            {claim?.claimId
              ? getTrimmedUuid(claim?.claimId)
              : claim?.claim
                ? getTrimmedUuid(getIdFromObj(claim.claim) ?? "")
                : "-"}
          </Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>{claim?.payersClaimId ?? "-"}</Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>{claim.patientName ?? "-"}</Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>
            {(claim.provider || claim?.providerName) ?? "-"}
          </Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>{claim.paidAs ?? "-"}</Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>{`$ ${totalBill.toFixed(2)}`}</Text>
        </TableCell>
        <TableCell>
          <PostingStatusBadge status={status} />
        </TableCell>
        {/* ECRH-4700 — claim-level Post removed; posting is per CPT line in the
            expanded Service Details table below. */}
        {/* <TableCell align="center">
          <ActionMenu
            options={[
              { value: "view_eob_details", label: "View EOB Details" },
              { value: "edit_eob", label: "Edit EOB" },
              {
                value: "post_payment",
                label: "Post Payment",
                isDisabled: !eligible || isPosted || !hasPostPermission,
                hoverMsg: isPosted
                  ? "Already posted"
                  : !eligible
                    ? "Not eligible"
                    : "Insufficient permission",
              },
            ]}
            onActionClick={(v) => onClaimAction(v, claim)}
          />
        </TableCell> */}
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={10}
          sx={{
            p: 0,
            borderBottom: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, py: 2, bgcolor: "grey.50" }}>
              {/* <Text mb={1} fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                Procedure Details — {claim.claimId ?? claimKeyValue}
              </Text> */}
              <EraComputedProcedureSubTable
                procedures={claim.patientProcedureCodes ?? []}
                groupCode={claim.groupCode}
                onPostCpt={onPostCpt}
                onEditCpt={onEditCpt}
                postDisabled={!hasPostPermission}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default EraClaimsRow;
